import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import { Button } from '@upmc/ims.component-library';
import { UserContext } from 'context/User/UserContext';
import { nanoid } from 'nanoid';
import { useCallback, useContext, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ACTION_TYPES } from '../../components/UI/MDC/DataTable/_types';
import { DataTable } from '../../components/UI/MDC/DataTable/DataTable';
import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import PersonIcon from '@mui/icons-material/Person';
import Icon from '../../components/UI/MDC/Icon/Icon';
import {
	Subtitle1,
	Subtitle2,
} from '../../components/UI/MDC/Typography/Typography';
import { NOTIFICATION_TYPES } from '../../constants/notifications';
import { addMessage } from '../../store/ancillary/actions';
import {
	filterByUser,
	indexRows,
	init,
	setRows,
	sortByHeader,
	toggleAll,
	toggleRow,
} from '../../store/pendingApprovals/actions';
import { getPendingApprovalsForApprover } from './_api';
import { HEADERS, HEADER_DEFAULT_SORT, HEADER_FILTER_BY } from './_constants';
import classes from './PendingApprovals.module.scss';

/**
 * React component for listing pending approvals and handling batch approvals.
 * @param {object} props Props for controlling modal components associated with the table.
 * @returns React component.
 */
const PendingApprovalsTable = ({
	setNavigateUrl,
	setApprovalEmailOpen,
	setConfirmationModalOpen,
	externalRefreshValue,
	externalLoadingValue,
}) => {
	// Redux store
	const headers = useSelector(state => state.pendingApprovals.headers);
	const actions = useSelector(state => state.pendingApprovals.actions);
	const rows = useSelector(state => state.pendingApprovals.rows);

	// Main table
	const [isLoading, setIsLoading] = useState(true);
	const [lastRefreshValue, setLastRefreshValue] = useState(0);
	const [lastLoadingValue, setLastLoadingValue] = useState(0);
	const [displayedRows, setDisplayedRows] = useState(rows);
	const [selectedCount, setSelectedCount] = useState(0);
	const [refreshDate, setRefreshDate] = useState();
	const [curFilteredOptions, setCurFilteredOptions] = useState([]);
	const [curSearchValue, setCurSearchValue] = useState('');
	const [curFilterValue, setCurFilterValue] = useState([]);

	// User info
	const contextUser = useContext(UserContext);
	const user =
		contextUser.userInfo.impersonationProfile ??
		contextUser.userInfo.userProfile;

	const dispatch = useDispatch();

	// Sets url to navigate to and opens up modal
	const rowOnClickHandler = useCallback(
		url => {
			setNavigateUrl(url);
			setApprovalEmailOpen(true);
		},
		[setNavigateUrl, setApprovalEmailOpen]
	);

	// Retrieves pending approvals using API
	const refreshTable = useCallback(
		(userId, searchValue, filterValue) => {
			// Lock table while data is loading
			setIsLoading(true);

			// Update last refreshed date
			setRefreshDate(new Date().toLocaleString());

			// Call API
			getPendingApprovalsForApprover(userId)
				.then(payload => {
					// Set row values
					dispatch(
						setRows(
							payload.map(request => ({
								ActionCells: [
									{
										BindTo: 0,
										Checked: false,
										Key: nanoid(),
										Text: 'Toggle Selection',
										Type: ACTION_TYPES.CHECKBOX,
									},
								],
								Cells: {
									[HEADERS.HEADER_REQUEST_FOR]: {
										Value: request.RequestFor,
										Validation: {
											Key: 'true',
											Value: null,
											memberToValidateKey: null,
										},
										props: {
											onClick: () =>
												rowOnClickHandler(
													request.NavigateURL
												),
											leadingIcon: (
												<Tooltip
													slotProps={{
														tooltip: {
															onClick: e =>
																e.stopPropagation(),
														},
													}}
													leaveDelay={750}
													placement='top'
													title={
														<Box>
															Network ID:{' '}
															{request.NetworkID}
															<br />
															Job Title:{' '}
															{request.JobTitle}
															<br />
															User Type:{' '}
															{request.UserType}
														</Box>
													}
												>
													<PersonIcon
														sx={{
															marginRight: '4px',
															verticalAlign:
																'bottom',
														}}
														onClick={e =>
															e.stopPropagation()
														}
														fontSize='small'
													/>
												</Tooltip>
											),
										},
									},
									[HEADERS.HEADER_REQUESTED_BY]: {
										Value: request.RequestedBy,
										Validation: {
											Key: 'true',
											Value: null,
											memberToValidateKey: null,
										},
										props: {
											onClick: () =>
												rowOnClickHandler(
													request.NavigateURL
												),
										},
									},
									[HEADERS.HEADER_REQUESTED_DATE]: {
										Value: new Date(
											request.RequestedDate
										).toLocaleString(),
										Validation: {
											Key: 'true',
											Value: null,
											memberToValidateKey: null,
										},
										props: {
											onClick: () =>
												rowOnClickHandler(
													request.NavigateURL
												),
										},
									},
									[HEADERS.HEADER_APPROVAL_FOR]: {
										Value: request.ApprovalFOR,
										Validation: {
											Key: 'true',
											Value: null,
											memberToValidateKey: null,
										},
										props: {
											onClick: () =>
												rowOnClickHandler(
													request.NavigateURL
												),
										},
									},
									[HEADERS.HEADER_JUSTIFICATION]: {
										Value: request.Justification,
										Validation: {
											Key: 'true',
											Value: null,
											memberToValidateKey: null,
										},
										props: {
											className:
												classes[
													'data-table__cell--justification'
												],
											extendtext: 75,
											cellTextOnClick: () =>
												rowOnClickHandler(
													request.NavigateURL
												),
										},
									},
									[HEADERS.HEADER_TRACKING_NUMBER]: {
										Value: request.Tracking_Number,
										Validation: {
											Key: 'true',
											Value: null,
											memberToValidateKey: null,
										},
										props: {
											extendtext: 13,
											cellTextOnClick: () =>
												rowOnClickHandler(
													request.NavigateURL
												),
										},
									},
								},
								props: {
									className: classes['cursor--pointer'],
								},
								Key: request.event_id,
								approvalType: request.ApprovalType,
								displaySearched: true,
								displayFiltered: true,
							})),
							searchValue,
							filterValue
						)
					);
				})
				.catch(e =>
					// Display error message
					dispatch(
						addMessage({
							Key: NOTIFICATION_TYPES.ERROR,
							Value: `An error has occurred while attempting to retrieve pending approvals data. ${e.message}.`,
						})
					)
				)
				.finally(() =>
					// Stop loading table
					setIsLoading(false)
				);
		},
		[dispatch, rowOnClickHandler]
	);

	// Initialize header and actions
	useEffect(() => {
		// Initialize redux store
		dispatch(
			init({
				headers: Object.values(HEADERS).map(header => ({
					Value: header,
					props: {
						onSortChange: sortOrder =>
							dispatch(sortByHeader(header, sortOrder)),
						sort: header === HEADER_DEFAULT_SORT ? 1 : null,
					},
				})),
				actions: [
					{
						Type: ACTION_TYPES.CHECKBOX,
						onChangedHandler: (_, __, row) =>
							dispatch(toggleRow(row)),
						Header: {
							Checked: false,
							Key: '0',
							onChangedHandler: e => dispatch(toggleAll(e, 0)),
						},
					},
				],
				headerFilterBy: HEADER_FILTER_BY,
			})
		);
	}, [dispatch]);

	// Initialize filter options
	useEffect(
		() =>
			setCurFilteredOptions(
				rows
					.map(row => row.Cells[HEADER_FILTER_BY].Value)
					.filter((item, index, self) => self.indexOf(item) === index)
					.sort()
			),
		[setCurFilteredOptions, rows]
	);

	// Retrieve data on load or change of user
	useEffect(() => {
		refreshTable(user.ID, '', []);
	}, [refreshTable, user.ID]);

	// Refresh table due to external trigger
	useEffect(() => {
		// Refresh only if refresh value kept internally differs from the externally obtained value
		// We do not want to refresh data yet
		if (externalRefreshValue !== lastRefreshValue) {
			setLastRefreshValue(externalRefreshValue);
			refreshTable(user.ID, curSearchValue, curFilterValue);
		}
	}, [
		curFilterValue,
		curSearchValue,
		externalRefreshValue,
		lastRefreshValue,
		refreshTable,
		user.ID,
	]);

	// Set table to loading due to external trigger
	useEffect(() => {
		// Refresh only if refresh value kept internally differs from the externally obtained value
		if (externalLoadingValue !== lastLoadingValue) {
			// Set table to loading to lock it up and avoid resubmission of requests data
			setLastLoadingValue(externalLoadingValue);
			setIsLoading(true);
		}
	}, [
		externalLoadingValue,
		externalRefreshValue,
		lastLoadingValue,
		lastRefreshValue,
	]);

	// Update displayed and get selected count
	useEffect(() => {
		// Get new displayed rows
		const newDisplayedRows = rows.filter(
			row => row.displaySearched && row.displayFiltered
		);
		setDisplayedRows(newDisplayedRows);

		// Count selected
		setSelectedCount(
			newDisplayedRows.filter(row => row.ActionCells[0].Checked === true)
				.length
		);
	}, [rows]);

	// Return React component
	return (
		<>
			{(isLoading || rows.length > 0) && (
				<DataTable
					loading={isLoading}
					headers={headers}
					rows={displayedRows}
					actions={actions}
					indexed={true}
					filtered={curFilteredOptions.length > 0}
					filteredOptions={curFilteredOptions}
					onChipSetChangedHandler={filter => {
						dispatch(filterByUser(filter));
						setCurFilterValue(filter);
					}}
					searchfieldText='Search...'
					resetSearchValue={curSearchValue}
					onTextFieldChangedHandler={e => {
						setCurSearchValue(e.target.value);
						dispatch(indexRows(e.target.value));
					}}
				/>
			)}

			<Grid container direction='row' justifyContent='space-between'>
				<Grid item>
					{isLoading || rows.length > 0 ? (
						<>
							<Subtitle2>
								<b>{`${selectedCount}/${displayedRows.length}`}</b>
								{' rows selected'}
							</Subtitle2>
							<Button
								className={classes['button--margin--right']}
								disabled={isLoading || selectedCount <= 0}
								onClick={() => setConfirmationModalOpen(true)}
							>
								Approve Request(s)
							</Button>
						</>
					) : (
						<Subtitle1>
							There are no request(s) pending your approval.
						</Subtitle1>
					)}
				</Grid>
				<Grid item>
					<IconButton
						disabled={isLoading}
						sx={{ float: 'right' }}
						onClick={() =>
							refreshTable(
								user.ID,
								curSearchValue,
								curFilterValue
							)
						}
					>
						<Icon icon='refresh' />
					</IconButton>
					<Subtitle2
						className={classes['subTitle--refresh--default']}
					>
						<b>Last Refreshed: </b>
						{refreshDate != null ? refreshDate : 'ERROR'}
					</Subtitle2>
				</Grid>
			</Grid>
		</>
	);
};

export { PendingApprovalsTable };
